// src/components/tabs/InvoicesTab.jsx
import React from 'react';
import { Filter, RefreshCw, Search, Eye, Edit, Target, AlertCircle } from 'lucide-react';
import FilterBar from '../shared/FilterBar';
import Hero from '../shared/Hero';

const InvoicesTab = ({
  invoices,
  rawInvoices,
  filters,
  setFilters,
  formatCurrency,
  hasIssues,
  onOpenPdf,
}) => {
  return (
    <div>
      {/* Hero institucional */}
      <Hero
        title="Validação de Faturas"
        subtitle="finance@invoices.martinscastro.pt"
        icons={['/icons/document-eu.png', '/icons/passport-pt.png', '/icons/globe.png']}
      />

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 mb-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Filter className="w-5 h-5 text-purple-600" />
            Smart Filters
          </h3>
          <button
            onClick={() => setFilters({ status: '', supplier: '', dateFrom: '', dateTo: '' })}
            className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
            aria-label="Reset filters"
          >
            <RefreshCw className="w-4 h-4" />
            Reset all
          </button>
        </div>

        <FilterBar
          filters={filters}
          onChange={(next) => setFilters({ ...filters, ...next })}
          leftSlot={
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="pending_validation">Pending</option>
              <option value="validated">Validated</option>
              <option value="exported">Exported</option>
            </select>
          }
          middleSlot={
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search supplier..."
                value={filters.supplier}
                onChange={(e) => setFilters({ ...filters, supplier: e.target.value })}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          }
          rightSlot={
            <div className="grid grid-cols-2 gap-4">
              <input
                type="date"
                placeholder="From date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="date"
                placeholder="To date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          }
        />
      </div>

      {/* Invoice List */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Invoice #
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Match
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className={`hover:bg-gray-50 transition-colors ${hasIssues(invoice) ? 'bg-red-50' : ''}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {invoice.invoice_date || (
                        <span className="text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          Missing
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {invoice.supplier_name === 'UNKNOWN' ? (
                        <span className="text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          Unknown Supplier
                        </span>
                      ) : (
                        invoice.supplier_name
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {invoice.invoice_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {(invoice.total_amount || 0) === 0 ? (
                        <span className="text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {formatCurrency(0, invoice.currency)}
                        </span>
                      ) : (
                        formatCurrency(invoice.total_amount, invoice.currency)
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-3 py-1 text-xs font-medium rounded-full
                      ${
                        invoice.status === 'pending_validation'
                          ? 'bg-amber-100 text-amber-700'
                          : invoice.status === 'validated'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {invoice.status === 'pending_validation'
                        ? 'Pending'
                        : invoice.status === 'validated'
                        ? 'Validated'
                        : 'Exported'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {invoice.matched_transaction_id ? (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-xs text-green-700 font-medium">
                          {invoice.matching_confidence}% match
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full" />
                        <span className="text-xs text-red-700">Unmatched</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onOpenPdf(invoice)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="View PDF"
                        aria-label="View PDF"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {invoices.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    Nenhuma fatura encontrada com os filtros atuais.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InvoicesTab;
